import {
  quarterPrefixFromPeriod,
  todayDateString,
} from "./sponsorEngagementLaunch";

export type TierName =
  | "Champion"
  | "Active"
  | "Developing"
  | "At-Risk"
  | string;

/** Canonical theater codes for sponsor engagement filters. */
export const LEADERBOARD_TIER_ORDER = [
  "Champion",
  "Active",
  "Developing",
  "At-Risk",
] as const;

export const SPONSOR_ENGAGEMENT_THEATERS = ["AMER", "APJC", "EMEA"] as const;

export type SponsorEngagementTheater =
  (typeof SPONSOR_ENGAGEMENT_THEATERS)[number];

const THEATER_ALIASES: Record<string, SponsorEngagementTheater> = {
  AMER: "AMER",
  AMERICAS: "AMER",
  AMERICA: "AMER",
  "LATIN AMERICA": "AMER",
  EMEA: "EMEA",
  APJC: "APJC",
  APAC: "APJC",
  "ASIA PACIFIC": "APJC",
};

export function normalizeTheaterCode(
  value: string | null | undefined
): SponsorEngagementTheater | null {
  const key = (value || "").trim().toUpperCase();
  if (!key || key === "—") return null;
  if (key in THEATER_ALIASES) return THEATER_ALIASES[key];
  return (SPONSOR_ENGAGEMENT_THEATERS as readonly string[]).includes(key)
    ? (key as SponsorEngagementTheater)
    : null;
}

export function rowMatchesTheaterFilter(
  rowTheater: string | null | undefined,
  filterTheater: string
): boolean {
  if (filterTheater === "All") return true;
  return normalizeTheaterCode(rowTheater) === filterTheater;
}

export function computeLeaderboardTierChips(
  rows: { tier?: string | null }[]
): { tier: string; count: number; pct: number }[] {
  const counts: Record<string, number> = {};
  LEADERBOARD_TIER_ORDER.forEach(t => {
    counts[t] = 0;
  });
  rows.forEach(r => {
    const tier = r.tier || "Developing";
    counts[tier] = (counts[tier] ?? 0) + 1;
  });
  const total = rows.length || 1;
  return LEADERBOARD_TIER_ORDER.map(tier => ({
    tier,
    count: counts[tier] ?? 0,
    pct: Math.round(((counts[tier] ?? 0) / total) * 100),
  }));
}

export function formatQuarterPeriodLabel(period: string): string {
  const trimmed = period.trim();
  if (!trimmed) return "—";
  const prefix = quarterPrefixFromPeriod(trimmed);
  const yearMatch = trimmed.match(/^(\d{4})/);
  const year = yearMatch?.[1];
  if (year && prefix) return `${prefix} ${year}`;
  return trimmed;
}

function parseQuarterPeriod(period: string): [number, number] {
  const match = period.trim().match(/^(\d{4})-Q(\d)$/i);
  if (!match) return [0, 0];
  return [Number(match[1]), Number(match[2])];
}

export function sortQuarterPeriodsDesc(periods: string[]): string[] {
  return [...periods].sort((a, b) => {
    const [ay, aq] = parseQuarterPeriod(a);
    const [by, bq] = parseQuarterPeriod(b);
    if (ay !== by) return by - ay;
    return bq - aq;
  });
}

/** Merge the viewed period with published historical periods for quarter pickers. */
export function mergeLeaderboardPeriodOptions(
  viewedPeriod?: string,
  availablePeriods?: string[]
): string[] {
  const periods: string[] = [];
  const addPeriod = (period?: string) => {
    const normalized = period?.trim();
    if (normalized && !periods.includes(normalized)) periods.push(normalized);
  };

  addPeriod(viewedPeriod);
  availablePeriods?.forEach(p => {
    addPeriod(p);
  });
  return sortQuarterPeriodsDesc(periods);
}

/** Union all quarter sources for leaderboard / snapshot pickers. */
export function buildLeaderboardQuarterOptions(
  ...sources: (string | string[] | null | undefined)[]
): string[] {
  const flat: string[] = [];
  for (const source of sources) {
    if (!source) continue;
    if (Array.isArray(source)) {
      flat.push(...source);
    } else {
      flat.push(source);
    }
  }
  return mergeLeaderboardPeriodOptions(undefined, flat);
}

/** Empty-state copy for the published leaderboard table when a quarter has no rows. */
export function getPublishedLeaderboardEmptyMessage(
  period?: string,
  publicationState?: string | null,
  availablePeriods?: string[]
): string {
  const label = period ? formatQuarterPeriodLabel(period) : "this quarter";
  const hasOtherPublishedQuarters =
    typeof period === "string" &&
    period.length > 0 &&
    (availablePeriods?.length ?? 0) > 0 &&
    !availablePeriods?.includes(period);

  if (!publicationState) {
    const hint = hasOtherPublishedQuarters
      ? " Select a previous quarter above to view published rankings."
      : "";
    return `No published leaderboard for ${label} yet.${hint}`;
  }

  if (publicationState === "pending_approval") {
    return `The ${label} leaderboard is awaiting CCO approval before it can be published.`;
  }

  if (publicationState === "draft") {
    return `The ${label} leaderboard is in draft and has not been published yet.`;
  }

  return `No published rankings for ${label}.`;
}

const TIER_COLOR_MAP: Record<string, string> = {
  Champion: "bg-green-100 text-green-800",
  Active: "bg-blue-100 text-blue-800",
  Developing: "bg-amber-100 text-amber-900",
  "At-Risk": "bg-red-100 text-red-900",
};

const TIER_DOT_MAP: Record<string, string> = {
  Champion: "bg-green-800",
  Active: "bg-blue-700",
  Developing: "bg-amber-800",
  "At-Risk": "bg-red-800",
};

export const TIER_PILL_BASE =
  "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap";

export function tierClass(tier: TierName | null | undefined): string {
  if (!tier) return "";
  return TIER_COLOR_MAP[tier] ?? "";
}

export function tierDotClass(tier: TierName | null | undefined): string {
  if (!tier) return "w-1.5 h-1.5 rounded-full bg-gray-400";
  return `w-1.5 h-1.5 rounded-full shrink-0 ${TIER_DOT_MAP[tier] ?? "bg-gray-400"}`;
}

export function tierPillClass(
  tier: TierName | null | undefined,
  extra = ""
): string {
  return `${TIER_PILL_BASE} ${tierClass(tier)} ${extra}`.trim();
}

export function statusBadgeClass(
  status: "completed" | "pending" | "draft" | "overdue"
): string {
  const base =
    "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium uppercase whitespace-nowrap";
  const variants: Record<string, string> = {
    completed: "bg-green-100 text-green-800",
    pending: "bg-amber-100 text-amber-900",
    draft: "bg-gray-100 text-gray-600",
    overdue: "bg-red-100 text-red-900",
  };
  return `${base} ${variants[status]}`;
}

export function deltaClass(direction: "up" | "down" | "flat"): string {
  const base = "text-[11px] font-bold inline-flex items-center gap-0.5";
  if (direction === "up") return `${base} text-green-800`;
  if (direction === "down") return `${base} text-red-800`;
  return `${base} text-gray-500`;
}

export function notificationBannerClass(
  variant: "amber" | "green" | "purple"
): string {
  const base =
    "py-3 px-4 rounded-lg flex items-center justify-start gap-2.5 border text-sm";
  const variants: Record<string, string> = {
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    green: "bg-green-50 border-green-200 text-green-800",
    purple: "bg-purple-50 border-purple-200 text-purple-900",
  };
  return `${base} ${variants[variant]}`;
}

/** @deprecated Use Components/Button instead */
export function mciButtonClass(
  variant: "primary" | "secondary" | "ghost",
  size: "sm" | "md" | "lg" = "md"
): string {
  const base =
    "inline-flex items-center gap-1.5 rounded-md font-semibold cursor-pointer border border-transparent font-inherit transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed";
  const sizes: Record<string, string> = {
    sm: "py-1 px-2.5 text-xs",
    md: "py-[7px] px-3.5 text-[13px]",
    lg: "py-2 px-[18px] text-sm",
  };
  const variants: Record<string, string> = {
    primary:
      "bg-purple-600 text-white border-purple-600 hover:enabled:bg-purple-700",
    secondary:
      "bg-white text-gray-900 border-gray-200 hover:enabled:bg-gray-100",
    ghost:
      "bg-transparent text-purple-600 border-transparent hover:enabled:bg-purple-50",
  };
  return `${base} ${sizes[size]} ${variants[variant]}`;
}

export const MCI_MUTED = "text-gray-500";

export const CFG_SECTION =
  "bg-white border border-gray-200 rounded-lg px-[18px] py-4 mb-3.5 shadow-sm";
export const CFG_SECTION_H = "text-[13.5px] font-bold text-gray-800 mb-1";
export const CFG_SECTION_SUB =
  "text-[11.5px] text-gray-500 mb-3.5 leading-relaxed";
export const CFG_CONTROL_ROW =
  "grid grid-cols-[200px_1fr] gap-3.5 items-center mb-[11px] text-[12.5px] [&_label]:mb-0 [&_label]:text-sm [&_label]:font-medium [&_label]:text-gray-700 [&_select]:w-[140px] [&_select]:max-w-full [&_select]:min-h-10 [&_select]:py-2 [&_select]:px-3 [&_select]:border [&_select]:border-[#dee2e6] [&_select]:rounded-lg [&_select]:text-sm [&_select]:font-medium [&_select]:bg-white [&_select]:outline-none [&_select]:focus:border-[#974ae4] [&_select]:focus:shadow-[0_0_0_1px_#974ae4] [&_input[type=number]]:w-[140px] [&_input[type=number]]:min-h-10 [&_input[type=number]]:py-2 [&_input[type=number]]:px-3 [&_input[type=number]]:border [&_input[type=number]]:border-[#dee2e6] [&_input[type=number]]:rounded-lg [&_input[type=number]]:text-sm [&_input[type=number]]:font-medium [&_input[type=number]]:bg-white [&_input[type=number]]:outline-none [&_input[type=number]]:focus:border-[#974ae4] [&_input[type=number]]:focus:shadow-[0_0_0_1px_#974ae4]";
export const CFG_WEIGHT_ROW =
  "grid grid-cols-[160px_1fr_70px] items-center gap-3.5 mb-2.5 last:mb-0";
export const CFG_WEIGHT_LBL =
  "text-[13px] text-gray-700 font-semibold flex items-center gap-1.5";
export const CFG_WEIGHT_SLIDER = "w-full accent-purple-600";
export const CFG_WEIGHT_VAL =
  "text-[13px] font-bold text-gray-800 text-right tabular-nums";
export const CFG_TIER_TABLE =
  "w-full border-collapse text-[12.5px] [&_th]:px-2 [&_th]:py-[7px] [&_th]:text-[10.5px] [&_th]:font-bold [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-gray-500 [&_th]:text-left [&_th]:border-b [&_th]:border-gray-200 [&_th]:bg-gray-50 [&_td]:px-2 [&_td]:py-[7px] [&_td]:border-b [&_td]:border-gray-100 [&_td]:tabular-nums [&_tr:last-child_td]:border-b-0 [&_input]:w-[60px] [&_input]:min-h-8 [&_input]:px-2 [&_input]:py-1.5 [&_input]:border [&_input]:border-[#dee2e6] [&_input]:rounded-lg [&_input]:bg-white [&_input]:text-xs [&_input]:font-medium [&_input]:text-right [&_input]:outline-none [&_input]:focus:border-[#974ae4] [&_input]:focus:shadow-[0_0_0_1px_#974ae4]";

/** Poll 1 / Poll 2 tier maps — fixed columns so both tables align. */
export const CFG_TIER_MAP_TABLE = `${CFG_TIER_TABLE} table-fixed [&_th:nth-child(2)]:text-center [&_td:nth-child(2)]:text-center [&_td]:align-middle`;
export const CFG_EVIDENCE_TABLE =
  "w-full border-collapse text-[12.5px] [&_th]:bg-gray-50 [&_th]:px-2.5 [&_th]:py-2 [&_th]:text-[10.5px] [&_th]:font-bold [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-gray-500 [&_th]:text-left [&_th]:border-b [&_th]:border-gray-200 [&_th.center]:text-center [&_td]:px-2.5 [&_td]:py-2 [&_td]:border-b [&_td]:border-gray-200 [&_td]:align-middle [&_td.center]:text-center [&_tr:last-child_td]:border-b-0 [&_select]:px-2 [&_select]:py-1 [&_select]:border [&_select]:border-gray-300 [&_select]:rounded [&_select]:text-xs [&_select]:bg-white [&_select]:cursor-pointer [&_input[type=checkbox]]:w-4 [&_input[type=checkbox]]:h-4 [&_input[type=checkbox]]:cursor-pointer [&_input[type=checkbox]]:accent-purple-600";
export const CFG_EVIDENCE_SUBHDR =
  "text-[11.5px] font-bold text-gray-700 uppercase tracking-wide mt-3.5 mb-1.5 first:mt-0";
export const CFG_QTEXT =
  "font-semibold text-gray-800 max-w-[380px] leading-snug";
export const CFG_QMETA = "text-[10.5px] text-gray-500 mt-px font-normal";
export const CFG_PAGE_HEADER =
  "flex items-start justify-between gap-4 mb-[18px]";
export const CFG_PAGE_TITLE =
  "text-xl font-bold text-gray-800 tracking-tight mb-0.5";
export const CFG_PAGE_SUB = "text-[12.5px] text-gray-500 mb-0";
export const CFG_ACTIONS =
  "flex gap-2 justify-end pt-3.5 border-t border-gray-200 mt-1.5";
export const CFG_WEIGHT_SUM =
  "py-[9px] px-[11px] bg-purple-50 border border-purple-200 rounded-md flex items-center justify-between text-xs text-purple-800 mt-2 font-semibold";
export const CFG_WEIGHT_SUM_WARN =
  "bg-amber-50 border-amber-300 text-amber-900";

export const LB_TABLE_CARD =
  "bg-white border border-gray-200 rounded-lg overflow-hidden";

export const LB_TABLE_WRAP = "overflow-x-auto";

export const LB_TABLE =
  "w-full text-sm table-fixed min-w-[1000px] border-collapse [&_tbody_tr:last-child_td]:border-b-0";

export const LB_TH =
  "bg-gray-50 py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200 cursor-pointer select-none align-middle whitespace-nowrap hover:text-gray-700";

export const LB_TH_SORTED = "text-purple-600 hover:text-purple-600";

export const LB_SORT_IND = "text-[9px] ml-0.5 opacity-70";

export function leaderboardColAlignClass(
  align: "center" | "left" | "right"
): string {
  if (align === "center") return "text-center";
  if (align === "right") return "text-right tabular-nums";
  return "text-left";
}

export function leaderboardRowClassName(_rank?: number): string {
  return "group cursor-pointer hover:bg-gray-50";
}

export function leaderboardRowCellClassName(_rank?: number): string {
  return "box-border bg-white group-hover:bg-purple-50 px-4 py-4 border-b border-gray-200 align-middle overflow-hidden text-ellipsis";
}

export const LB_GAP_CELL =
  "relative pr-7 after:content-['→'] after:absolute after:right-2.5 after:top-1/2 after:-translate-y-1/2 after:text-purple-600 after:font-bold after:text-[15px] after:pointer-events-none after:leading-none";

export const LB_RANK_MEDAL =
  "inline-flex items-center justify-center w-6 h-6 shrink-0 mx-auto text-2xl leading-none";

export const LB_RANK_BADGE =
  "inline-flex items-center justify-center w-5 h-5 shrink-0 mx-auto rounded-full bg-gray-900 text-white text-[10px] font-bold tabular-nums leading-none";

export const LB_SPONSOR_NAME = "font-semibold text-gray-900";

export const LB_SPONSOR_GL = "text-[10.5px] text-gray-500";

export function gapFlagClass(warn = false): string {
  const base =
    "inline-flex items-center gap-0.5 py-0.5 px-2 rounded-lg text-[10px] font-bold tracking-wide";
  return warn
    ? `${base} bg-amber-100 text-amber-900`
    : `${base} bg-red-100 text-red-900`;
}

export function leaderboardDeltaClass(
  direction: "up" | "down" | "flat"
): string {
  const base = "text-xs font-semibold tabular-nums";
  if (direction === "up") return `${base} text-green-700`;
  if (direction === "down") return `${base} text-red-700`;
  return `${base} text-gray-500 font-medium`;
}

export function fmtScore(value: number | null | undefined, digits = 2): string {
  if (value == null || Number.isNaN(value)) return "—";
  return value.toFixed(digits);
}

export function fmtPct(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

export function sponsorInitials(name: string | null | undefined): string {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .map(part => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function leaderboardStateTitle(
  pubState: string | null | undefined,
  period: string
): string {
  if (pubState === "published") return `Published Leaderboard · ${period}`;
  if (pubState === "pending_approval") return `Pending Approval · ${period}`;
  return `Draft Leaderboard · ${period}`;
}

function parseDateOnly(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/** Human-readable time remaining until a poll due date (ongoing collection). */
export function formatRemainingUntilDue(
  dueDate: string | null | undefined
): string | null {
  if (!dueDate) return null;
  const today = parseDateOnly(todayDateString());
  const due = parseDateOnly(dueDate);
  const diffDays = Math.round(
    (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (diffDays < 0) return null;
  if (diffDays === 0) return "Due today";
  if (diffDays === 1) return "1 day left";
  return `${diffDays} days left`;
}

/** True when countdown is due today or one day remaining (show in red). */
export function isUrgentPollCountdown(
  label: string | null | undefined
): boolean {
  if (!label) return false;
  return label === "Due today" || label === "1 day left";
}
